import * as React from "react";
import useSWR from "swr";
import { API } from "../../API";
import { isLoading, Loading, loading } from "../../Loading";
import { CaregiverAvailability, NullableCaregiverAvailability } from "../../messages/Caregiver";
import { retryableModalNetworkRequest } from "../../NetworkRequest";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import { withDefault } from "../../utils/withDefault";
import LoadingScaffold from "../LoadingScaffold";
import CaseSettingsScreen from "./CaseSettingsScreen";
import { initialCaregiverAvailability } from "./utils/initialCaregiverAvailability";

interface CaseSettingsActivityProps {
  api: API;
  appState: AppState;
  onClickGoBack: () => void;
}

const CaseSettingsActivity = (props: CaseSettingsActivityProps) => {
  const [availability, setAvailability] = React.useState<CaregiverAvailability | Loading>(
    loading()
  );
  const [error, setError] = React.useState<string>();

  const handleChangeAvailability = React.useCallback((newAvailability: CaregiverAvailability) => {
    setAvailability(newAvailability);
    setError(undefined);
  }, []);

  const handleClickGoBack = React.useCallback(async () => {
    if (!isLoading(availability)) {
      await retryableModalNetworkRequest(() => {
        return props.api.setCaregiverAvailability(availability);
      }, true);

      props.appState.refreshVisits();
      props.appState.refreshPatientTasks();
      props.appState.refreshTaskBroadcasts();
      props.appState.notificationState.triggerNotificationsRefresh();
    }

    props.onClickGoBack.call(null);
  }, [availability, props.api, props.appState, props.onClickGoBack]);

  const handleSuccessResponse = React.useCallback(
    (response: NullableCaregiverAvailability) =>
      setAvailability(withDefault(response.caregiverAvailability, initialCaregiverAvailability)),
    []
  );

  useSWR("caregiver/availability", () => props.api.getCaregiverAvailability(), {
    onSuccess: handleSuccessResponse,
    onError: setError,
  });

  if (isLoading(availability)) {
    return (
      <LoadingScaffold
        title={translate(STRINGS.CASE_SETTINGS)}
        onClickGoBack={props.onClickGoBack}
      />
    );
  }

  return (
    <CaseSettingsScreen
      availability={availability}
      error={error}
      onChangeAvailability={handleChangeAvailability}
      onClickGoBack={handleClickGoBack}
    />
  );
};

export default React.memo(CaseSettingsActivity);
